import { LAYOUT_HEADER_OFFSET_CSS_VAR, NAVBAR_HEIGHT_PX } from '@/constants/layoutHeaderInset';
import { AlertSystem } from '../services/AlertSystem';
import { Alert, AlertButton, AlertColors } from '../types';

const ALERT_TOP_GAP_PX = 12;

export class AlertUI {
  private alertSystem: AlertSystem;
  private container: HTMLDivElement | null;
  private isMobile: boolean;
  private mobileManuallyDismissed: boolean;

  constructor(alertSystem: AlertSystem) {
    this.alertSystem = alertSystem;
    this.container = null;
    this.isMobile = this.detectMobile();
    this.mobileManuallyDismissed = this.loadMobileDismissedState();
  }

  private loadMobileDismissedState(): boolean {
    try {
      const dismissed = localStorage.getItem('mobileAlertsDismissed');
      return dismissed === 'true';
    } catch (error) {
      return false;
    }
  }

  private saveMobileDismissedState(dismissed: boolean): void {
    try {
      localStorage.setItem('mobileAlertsDismissed', dismissed.toString());
    } catch (error) {
      console.error('Error saving mobile dismissed state:', error);
    }
  }

  private detectMobile(): boolean {
    return (
      window.innerWidth <= 768 ||
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    );
  }

  public init(): void {
    this.loadMobileStyles();
    this.createContainer();
    this.renderAlerts();
    this.setupResizeListener();
  }

  private loadMobileStyles(): void {
    if (!document.getElementById('mobile-alert-styles')) {
      const style = document.createElement('style');
      style.id = 'mobile-alert-styles';
      style.textContent = `
        @media (max-width: 768px) {
          #alerts-container {
            -webkit-overflow-scrolling: touch;
          }
          #alerts-container::-webkit-scrollbar {
            width: 4px;
          }
          #alerts-container::-webkit-scrollbar-track {
            background: transparent;
          }
          #alerts-container::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.2);
            border-radius: 2px;
          }
          .mobile-alert-trigger {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .mobile-alert-trigger:active {
            transform: scale(0.95) !important;
          }
          .alert-close, .alert-btn {
            min-height: 44px;
            min-width: 44px;
          }
          .alert {
            padding: 20px 16px !important;
          }
          .alert h4 {
            font-size: 18px !important;
            margin-bottom: 12px !important;
          }
          .alert p {
            font-size: 16px !important;
            line-height: 1.5 !important;
          }
          .alert-btn {
            padding: 12px 20px !important;
            font-size: 16px !important;
            border-radius: 8px !important;
            margin: 4px !important;
          }
        }
        @media (prefers-color-scheme: dark) {
          .mobile-alerts-header {
            background: #1a1a1a !important;
            border-bottom-color: #333 !important;
            color: white !important;
          }
          .mobile-alerts-header h3 {
            color: white !important;
          }
          .mobile-alerts-close {
            color: #ccc !important;
          }
          #alerts-container {
            background: rgba(0, 0, 0, 0.8) !important;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          #alerts-container, .mobile-alert-trigger, .alert {
            animation: none !important;
            transition: none !important;
          }
        }
        @media (prefers-contrast: high) {
          .mobile-alert-trigger {
            border: 2px solid white;
          }
          .alert {
            border: 2px solid currentColor !important;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }

  private setupResizeListener(): void {
    window.addEventListener('resize', () => {
      const wasMobile = this.isMobile;
      this.isMobile = this.detectMobile();

      if (wasMobile !== this.isMobile) {
        this.destroy();
        this.createContainer();
        this.renderAlerts();
      }
    });
  }

  private createContainer(): void {
    this.container = document.createElement('div');
    this.container.id = 'alerts-container';

    if (this.isMobile) {
      this.container.style.cssText = `
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 9999;
        max-height: 70vh;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 0;
        background: rgba(0, 0, 0, 0.1);
        backdrop-filter: blur(10px);
        padding: 0;
        transform: translateY(100%);
        transition: transform 0.3s ease-out;
      `;
    } else {
      this.container.style.cssText = `
        position: fixed;
        top: calc(max(${NAVBAR_HEIGHT_PX}px, var(${LAYOUT_HEADER_OFFSET_CSS_VAR}, ${NAVBAR_HEIGHT_PX}px)) + ${ALERT_TOP_GAP_PX}px);
        right: 20px;
        z-index: 9999;
        max-width: 350px;
        display: flex;
        flex-direction: column;
      `;
    }

    document.body.appendChild(this.container);
  }

  private getPopupAlertsToShow(): Alert[] {
    return this.alertSystem.getAlertsToShow().filter(alert => alert.isPopup || alert.type === 'aida');
  }

  private renderAlerts(): void {
    if (!this.container) return;

    const alerts = this.getPopupAlertsToShow();

    this.container.innerHTML = '';

    if (alerts.length === 0) {
      if (this.isMobile) {
        this.hideMobileContainer();
      }
      return;
    }

    if (this.isMobile) {
      this.createMobileHeader();
    }

    alerts.forEach(alert => {
      const alertElement = this.createAlertElement(alert);
      this.container?.appendChild(alertElement);
    });

    if (this.isMobile && !this.mobileManuallyDismissed) {
      setTimeout(() => {
        if (this.container) {
          this.container.style.transform = 'translateY(0)';
        }
      }, 100);
    }
  }

  private createMobileHeader(): void {
    if (!this.container || !this.isMobile) return;

    const header = document.createElement('div');
    header.className = 'mobile-alerts-header';
    header.style.cssText = `
      background: white;
      padding: 16px;
      border-bottom: 1px solid #e0e0e0;
      display: flex;
      justify-content: space-between;
      align-items: center;
      position: sticky;
      top: 0;
      z-index: 1;
    `;

    const alertCount = this.getPopupAlertsToShow().length;

    header.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px;">
        <div style="width: 32px; height: 4px; background: #ccc; border-radius: 2px;"></div>
        <h3 style="margin: 0; font-size: 16px; font-weight: 600; color: #333;">
          Notifications (${alertCount})
        </h3>
      </div>
      <button 
        class="mobile-alerts-close"
        style="
          background: none;
          border: none;
          color: #666;
          font-size: 24px;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: background 0.2s;
        "
      >
        ×
      </button>
    `;

    const closeBtn = header.querySelector('.mobile-alerts-close') as HTMLButtonElement;
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.mobileManuallyDismissed = true;
        this.saveMobileDismissedState(true);
        this.hideMobileContainer();
      });
    }

    this.container?.appendChild(header);
  }

  private hideMobileContainer(): void {
    if (!this.container || !this.isMobile) return;

    this.container.style.transform = 'translateY(100%)';
    setTimeout(() => {
      if (this.container) {
        this.container.innerHTML = '';
      }
    }, 300);
  }

  private createAlertElement(alert: Alert): HTMLDivElement {
    const div = document.createElement('div');
    div.className = `alert alert-${alert.type}`;
    div.setAttribute('data-alert-id', alert.id);

    const colors: Record<Alert['type'], AlertColors> = {
      info: { bg: '#e3f2fd', border: '#2196f3', text: '#0d47a1' },
      warning: { bg: '#fff3e0', border: '#ff9800', text: '#e65100' },
      error: { bg: '#ffebee', border: '#f44336', text: '#b71c1c' },
      success: { bg: '#e8f5e9', border: '#4caf50', text: '#1b5e20' },
      aida: { bg: '#fdf0f8', border: '#d02591', text: '#7a1456' },
    };

    const color = colors[alert.type];

    if (this.isMobile) {
      div.style.cssText = `
        background: white;
        border-left: 4px solid ${color.border};
        padding: 16px;
        border-bottom: 1px solid #e0e0e0;
        position: relative;
        animation: ${this.isMobile ? 'slideUpMobile' : 'slideIn'} 0.3s ease-out;
      `;
    } else {
      div.style.cssText = `
        background: ${color.bg};
        border-left: 4px solid ${color.border};
        padding: 16px;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        position: relative;
        animation: slideIn 0.3s ease-out;
        margin-bottom: 12px;
      `;
    }

    const buttonsHTML =
      alert.buttons && alert.buttons.length > 0
        ? `<div style="display: flex; gap: 8px; margin-top: 12px; flex-wrap: wrap;">
          ${alert.buttons.map((btn, index) => this.createButtonHTML(btn, index, color)).join('')}
         </div>`
        : '';

    const aidaAvatar =
      alert.type === 'aida'
        ? `<img
       src="/static/images/eyesAida.svg"
       alt="AIDA"
       style="width:28px;height:28px;object-fit:contain;flex-shrink:0;margin-top:2px;"
     />`
        : '';

    div.innerHTML = `
        <div style="display: flex; justify-content: space-between; align-items: start; gap: 10px;">
          ${aidaAvatar}
          <div style="flex: 1; padding-right: 10px;">
            <h4 style="margin: 0 0 4px 0; color: ${color.text}; font-size: 14px; font-weight: 600;">
              ${alert.title}
            </h4>
            <p style="margin: 0; color: ${color.text}; font-size: 13px; line-height: 1.45; opacity: 0.85;">
              ${alert.message}
            </p>
            ${buttonsHTML}
          </div>
          <button
            class="alert-close"
            style="
              background: none; border: none; color: ${color.text};
              font-size: 18px; cursor: pointer; padding: 0;
              width: 22px; height: 22px; display: flex;
              align-items: center; justify-content: center;
              border-radius: 4px; opacity: 0.6; flex-shrink: 0;
            "
          >×</button>
        </div>
      `;

    this.addAnimationStyles();

    const closeBtn = div.querySelector('.alert-close') as HTMLButtonElement;
    if (closeBtn) {
      closeBtn.addEventListener('mouseenter', () => {
        closeBtn.style.background = 'rgba(0,0,0,0.1)';
      });
      closeBtn.addEventListener('mouseleave', () => {
        closeBtn.style.background = 'none';
      });
      closeBtn.addEventListener('click', () => {
        this.dismissAlert(alert.id, div);
      });
    }

    if (alert.buttons) {
      alert.buttons.forEach((button, index) => {
        const btnElement = div.querySelector(`[data-alert-btn="${index}"]`) as HTMLButtonElement;
        if (btnElement) {
          this.attachButtonListener(btnElement, button);
        }
      });
    }

    return div;
  }

  private createButtonHTML(button: AlertButton, index: number, color: AlertColors): string {
    const styles = {
      primary: `
        background: ${color.border};
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: opacity 0.2s;
      `,
      secondary: `
        background: white;
        color: ${color.text};
        border: 1px solid ${color.border};
        padding: 8px 16px;
        border-radius: 4px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s;
      `,
      link: `
        background: none;
        color: ${color.border};
        border: none;
        padding: 8px 12px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        text-decoration: underline;
        transition: opacity 0.2s;
      `,
    };

    const style = styles[button.style || 'primary'];

    return `
      <button 
        class="alert-btn alert-btn-${button.style || 'primary'}"
        data-alert-btn="${index}"
        style="${style}"
      >
        ${button.text}
      </button>
    `;
  }

  private attachButtonListener(btnElement: HTMLButtonElement, button: AlertButton): void {
    btnElement.addEventListener('mouseenter', () => {
      if (button.style === 'primary' || button.style === 'link') {
        btnElement.style.opacity = '0.8';
      } else if (button.style === 'secondary') {
        btnElement.style.background = 'rgba(0,0,0,0.05)';
      }
    });

    btnElement.addEventListener('mouseleave', () => {
      if (button.style === 'primary' || button.style === 'link') {
        btnElement.style.opacity = '1';
      } else if (button.style === 'secondary') {
        btnElement.style.background = 'white';
      }
    });

    btnElement.addEventListener('click', e => {
      e.stopPropagation();

      const alertElement = btnElement.closest('[data-alert-id]') as HTMLDivElement;
      const alertId = alertElement?.getAttribute('data-alert-id');

      switch (button.action) {
        case 'link':
          if (button.url) {
            if (button.dismissOnClick && alertId && alertElement) {
              this.dismissAlert(alertId, alertElement);
              setTimeout(() => {
                window.location.href = button.url as string;
              }, 350);
            } else {
              window.location.href = button.url;
            }
          }
          break;

        case 'callback':
          if (button.callback) {
            button.callback();
          }
          if (button.dismissOnClick && alertId && alertElement) {
            this.dismissAlert(alertId, alertElement);
          }
          break;

        case 'modal':
          if (button.modalContent) {
            const event = new CustomEvent('openAlertModal', {
              detail: button.modalContent,
            });
            window.dispatchEvent(event);
          }
          if (button.dismissOnClick && alertId && alertElement) {
            this.dismissAlert(alertId, alertElement);
          }
          break;
      }
    });
  }

  private addAnimationStyles(): void {
    if (!document.getElementById('alert-animations')) {
      const style = document.createElement('style');
      style.id = 'alert-animations';
      style.textContent = `
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes slideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(400px);
            opacity: 0;
          }
        }
        @keyframes slideUpMobile {
          from {
            transform: translateY(50px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes slideDownMobile {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(50px);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }

  private dismissAlert(alertId: string, element: HTMLDivElement): void {
    const animation = this.isMobile ? 'slideDownMobile 0.3s ease-out' : 'slideOut 0.3s ease-out';
    element.style.animation = animation;

    setTimeout(() => {
      element.remove();
      this.alertSystem.dismissAlert(alertId);

      window.dispatchEvent(
        new CustomEvent('alertDismissed', {
          detail: { alertId, remainingCount: this.getAlertCount() },
        }),
      );

      if (this.isMobile && this.container) {
        const remainingAlerts = this.container.querySelectorAll('[data-alert-id]');
        if (remainingAlerts.length === 0) {
          this.hideMobileContainer();
        } else {
          const header = this.container.querySelector('.mobile-alerts-header h3');
          if (header) {
            header.textContent = `Notificaciones (${remainingAlerts.length})`;
          }
        }
      }
    }, 300);
  }

  public showMobileAlerts(): void {
    if (!this.isMobile || !this.container) return;

    this.mobileManuallyDismissed = false;
    this.saveMobileDismissedState(false);

    const hasContent = this.container.children.length > 0;

    if (!hasContent) {
      this.renderAlerts();
    } else {
      this.container.style.transform = 'translateY(0)';
    }
  }

  public hideMobileAlerts(): void {
    if (!this.isMobile) return;
    this.hideMobileContainer();
  }

  public refreshAlerts(): void {
    if (!this.container) return;
    this.renderAlerts();
  }

  public getAlertCount(): number {
    return this.alertSystem.getAlertsToShow().length;
  }

  public getPopupAlertCount(): number {
    return this.getPopupAlertsToShow().length;
  }

  public destroy(): void {
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }
}
